/**
 * Track 2: pure replay logic (no Pixi, no React) so the cinematic behaviour is unit-testable.
 * The battle engine is deterministic, so everything here derives only from the stored ships + event log.
 */
import type { BattleEvent, ReplayShip } from '@galaxis/shared';

export interface ShipState { ship: ReplayShip; currentHull: number; currentShield: number; destroyed: boolean }

export function applyEvent(ev: BattleEvent, state: Map<string, ShipState>): void {
  const target = ev.targetId ? state.get(ev.targetId) : null;
  if (!target) return;
  if (ev.type === 'attack') {
    if (ev.shieldDamage) target.currentShield = Math.max(0, target.currentShield - ev.shieldDamage);
    if (ev.hullDamage) target.currentHull = Math.max(0, target.currentHull - ev.hullDamage);
  } else if (ev.type === 'shield_break') {
    target.currentShield = 0;
  } else if (ev.type === 'ship_destroyed') {
    target.currentHull = 0; target.destroyed = true;
  }
}

/** The events up to `tick` (inclusive) applied to the initial ship values. */
export function stateAt(ships: ReplayShip[], log: BattleEvent[], tick: number): Map<string, ShipState> {
  const state = new Map<string, ShipState>(ships.map(s => [s.id, { ship: s, currentHull: s.maxHull, currentShield: s.maxShield, destroyed: false }]));
  for (const ev of log) {
    if (ev.tick > tick) break;
    applyEvent(ev, state);
  }
  return state;
}

export function lastTick(log: BattleEvent[]): number {
  let m = 0;
  for (const e of log) if (e.tick > m) m = e.tick;
  return m;
}

/** Ticks in which at least one ship died (sorted, unique) – the cinematic slow-motion and the timeline markers. */
export function killTicks(log: BattleEvent[]): number[] {
  return [...new Set(log.filter(e => e.type === 'ship_destroyed').map(e => e.tick))].sort((a, b) => a - b);
}

export function eventsAtTick(log: BattleEvent[], tick: number): BattleEvent[] {
  return log.filter(e => e.tick === tick);
}

/** Slow motion: how strongly time is slowed in the tick that has `kills` ship deaths (1 = normal speed). */
export function slowMoFactor(kills: number, isLastKill: boolean): number {
  if (kills <= 0) return 1;
  return isLastKill ? 0.18 : kills > 1 ? 0.28 : 0.35;
}

export interface Point { x: number; y: number }
export interface CameraTarget { x: number; y: number; zoom: number }

/**
 * Auto-camera: frame the ships involved in the recent events (attackers, targets), with padding, inside the arena.
 * `focus` are points of interest, `arena` the full scene size. Zoom is limited so the picture never becomes unreadable.
 */
export function computeFocus(focus: Point[], arena: { w: number; h: number }, opts: { maxZoom?: number; padding?: number } = {}): CameraTarget {
  const full: CameraTarget = { x: arena.w / 2, y: arena.h / 2, zoom: 1 };
  if (focus.length === 0) return full;
  const pad = opts.padding ?? 90, maxZoom = opts.maxZoom ?? 1.9;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of focus) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
  const bw = maxX - minX + pad * 2, bh = maxY - minY + pad * 2;
  const zoom = Math.max(1, Math.min(maxZoom, arena.w / bw, arena.h / bh));
  // keep the viewport inside the arena
  const halfW = arena.w / zoom / 2, halfH = arena.h / zoom / 2;
  const cx = Math.min(arena.w - halfW, Math.max(halfW, (minX + maxX) / 2));
  const cy = Math.min(arena.h - halfH, Math.max(halfH, (minY + maxY) / 2));
  return { x: cx, y: cy, zoom };
}

/** Ids of the ships that took part in events during the last `window` ticks up to `tick` (for the camera). */
export function recentActors(log: BattleEvent[], tick: number, window = 2): string[] {
  const ids = new Set<string>();
  for (const e of log) {
    if (e.tick > tick) break;
    if (e.tick <= tick - window) continue;
    if (e.type === 'battle_end') continue;
    if (e.attackerId) ids.add(e.attackerId);
    if (e.targetId) ids.add(e.targetId);
  }
  return [...ids];
}

/** Critically-damped-ish smoothing step (frame-rate independent): moves `cur` toward `target`. */
export function smoothStep(cur: number, target: number, dtMs: number, halfLifeMs = 220): number {
  return target + (cur - target) * Math.pow(0.5, dtMs / halfLifeMs);
}

export const REPLAY_SPEEDS = [0.5, 1, 2, 4] as const;
export type ReplaySpeed = typeof REPLAY_SPEEDS[number];
